import {
	createContext,
	useCallback,
	useContext,
	useLayoutEffect,
	useState,
} from "react"
import { getStoredTheme, getSystemTheme } from "@/utils/theme.helpers"

type Theme = "dark" | "light" | "system"
type ResolvedTheme = "dark" | "light"

type ThemeProviderProps = {
	children: React.ReactNode
	defaultTheme?: Theme
	storageKey?: string
}

type ThemeProviderState = {
	theme: Theme
	resolvedTheme: ResolvedTheme
	setTheme: (theme: Theme) => void
	systemTheme: ResolvedTheme
}

const initialState: ThemeProviderState = {
	theme: "system",
	resolvedTheme: "light",
	setTheme: () => null,
	systemTheme: "light",
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
	children,
	defaultTheme = "system",
	storageKey = "cardnexus-admin-theme",
	...props
}: ThemeProviderProps) {
	const [theme, setThemeState] = useState<Theme>(() =>
		getStoredTheme(storageKey, defaultTheme),
	)

	const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() =>
		getSystemTheme(),
	)

	// Calculate resolved theme
	const resolvedTheme: ResolvedTheme = theme === "system" ? systemTheme : theme

	const applyTheme = useCallback((targetTheme: ResolvedTheme) => {
		if (typeof window === "undefined") return

		const root = window.document.documentElement

		if (!root.classList.contains(targetTheme)) {
			root.classList.remove("light", "dark")
			root.classList.add(targetTheme)
		}
	}, [])

	useLayoutEffect(() => {
		applyTheme(resolvedTheme)
	}, [resolvedTheme, applyTheme])

	useLayoutEffect(() => {
		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")

		const handleSystemThemeChange = (e: MediaQueryListEvent) => {
			const newSystemTheme = e.matches ? "dark" : "light"
			setSystemTheme(newSystemTheme)
		}

		mediaQuery.addEventListener("change", handleSystemThemeChange)

		return () => {
			mediaQuery.removeEventListener("change", handleSystemThemeChange)
		}
	}, [])

	const setTheme = useCallback(
		(newTheme: Theme) => {
			try {
				if (typeof window !== "undefined") {
					window.localStorage.setItem(storageKey, newTheme)
				}
			} catch {
				// Silently ignore localStorage errors (e.g., in private mode)
			}
			setThemeState(newTheme)
		},
		[storageKey],
	)

	const value = {
		theme,
		resolvedTheme,
		systemTheme,
		setTheme,
	}

	return (
		<ThemeProviderContext.Provider {...props} value={value}>
			{children}
		</ThemeProviderContext.Provider>
	)
}

export const useTheme = () => {
	const context = useContext(ThemeProviderContext)

	if (context === undefined) {
		throw new Error("useTheme must be used within a ThemeProvider")
	}

	return context
}

export type { Theme, ResolvedTheme }
